import React, {useEffect} from "react";
import {useLocation} from "react-router-dom";

export function Tags({tags}: {tags: string[]}) {
    if (tags.length === 0) return null;
    return (
        <div className="pj-tags">
            {tags.map(t => <span key={t} className="pj-tag">{t}</span>)}
        </div>
    );
}

// Where the scroll container must be to show `el` at the top (honouring its
// scroll-margin-top). Uses layout offsets rather than getBoundingClientRect so
// the pages' slide-up entrance animation (a transform) doesn't skew it.
function scrollTargetFor(el: HTMLElement, scroller: HTMLElement) {
    let top = 0;
    let node: HTMLElement | null = el;
    while (node && node !== scroller) {
        top += node.offsetTop;
        node = node.offsetParent as HTMLElement | null;
    }
    if (node !== scroller) {
        // scroller isn't in the offsetParent chain: fall back to rects
        top = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
    }
    return top - (parseFloat(getComputedStyle(el).scrollMarginTop) || 0);
}

// Scrolls to the element named by the URL hash (e.g. #try-it-out,
// #delete-your-data). Runs after a tick so it lands after App's
// scroll-to-top on route change. A second check after the entrance animation
// only moves again if the layout really shifted (e.g. a late font) and the
// reader hasn't scrolled since.
const SETTLE_MS = 800;
export function useScrollToHash(enabled: boolean) {
    const {hash, pathname} = useLocation();
    useEffect(() => {
        if (!enabled || !hash) return;
        const id = decodeURIComponent(hash.slice(1));
        const find = () => {
            const el = document.getElementById(id);
            const scroller = el?.closest<HTMLElement>(".vignette");
            return el && scroller ? {el, scroller} : null;
        };
        let landedAt: number | null = null;
        const first = window.setTimeout(() => {
            const f = find();
            if (!f) return;
            f.scroller.scrollTo({top: scrollTargetFor(f.el, f.scroller)});
            landedAt = f.scroller.scrollTop;
        }, 50);
        const settle = window.setTimeout(() => {
            const f = find();
            if (!f || landedAt === null || Math.abs(f.scroller.scrollTop - landedAt) > 1) return;
            const target = scrollTargetFor(f.el, f.scroller);
            if (Math.abs(target - f.scroller.scrollTop) > 2) f.scroller.scrollTo({top: target});
        }, SETTLE_MS);
        return () => {
            window.clearTimeout(first);
            window.clearTimeout(settle);
        };
    }, [enabled, hash, pathname]);
}
